import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Navigation } from 'lucide-react';
import html2canvas from 'html2canvas';

interface CompassMeasurerProps {
  onDirectionCaptured: (direction: string, screenshotDataUrl?: string) => void;
  onClose: () => void;
}

export const CompassMeasurer: React.FC<CompassMeasurerProps> = ({
  onDirectionCaptured,
  onClose
}) => {
  const [currentHeading, setCurrentHeading] = useState<number>(0);
  const [isListening, setIsListening] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const headingHistoryRef = useRef<number[]>([]);
  const lastValidHeadingRef = useRef<number>(0);
  const screenshotRef = useRef<HTMLDivElement>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  // Large smoothing buffer for ultra-stable compass (like iPhone compass)
  const BUFFER_SIZE = 20;

  // Convert heading to cardinal direction (8 directions only)
  const getCardinalDirection = (heading: number): string => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(heading / 45) % 8;
    return directions[index];
  };

  // Get full direction name (8 directions only)
  const getFullDirectionName = (heading: number): string => {
    const directions = [
      'North', 'Northeast', 'East', 'Southeast',
      'South', 'Southwest', 'West', 'Northwest'
    ];
    const index = Math.round(heading / 45) % 8;
    return directions[index];
  };

  // Handle orientation event
  const handleOrientation = useCallback((event: DeviceOrientationEvent) => {
    let rawHeading: number | null = null;
    
    // iOS devices: Use webkitCompassHeading for true north
    if ((event as any).webkitCompassHeading !== undefined) {
      rawHeading = (event as any).webkitCompassHeading;
      console.log('[Compass] Using iOS webkitCompassHeading:', rawHeading);
    } 
    // Android/Other devices: Use alpha with absolute orientation
    else if (event.alpha !== null) {
      // Alpha is the compass heading (0-360)
      // 0/360 = North, 90 = East, 180 = South, 270 = West
      rawHeading = event.alpha;
      console.log('[Compass] Using standard alpha:', rawHeading);
    }
    
    if (rawHeading !== null) {
      // Normalize to 0-360 range
      if (rawHeading < 0) {
        rawHeading = 360 + rawHeading;
      }
      rawHeading = rawHeading % 360;
      
      // Add to smoothing buffer (no jump rejection for iPhone-like behavior)
      headingHistoryRef.current.push(rawHeading);
      if (headingHistoryRef.current.length > BUFFER_SIZE) {
        headingHistoryRef.current.shift();
      }

      // Calculate smoothed heading (circular mean for angles)
      const sinSum = headingHistoryRef.current.reduce((acc, val) => acc + Math.sin(val * Math.PI / 180), 0);
      const cosSum = headingHistoryRef.current.reduce((acc, val) => acc + Math.cos(val * Math.PI / 180), 0);
      let smoothed = Math.atan2(sinSum, cosSum) * 180 / Math.PI;
      
      // Normalize to 0-360
      if (smoothed < 0) {
        smoothed = 360 + smoothed;
      }
      
      lastValidHeadingRef.current = smoothed;
      setCurrentHeading(smoothed);
    }
  }, []);

  // Request permission and start listening
  const requestPermissionAndStart = useCallback(async () => {
    try {
      console.log('[Compass] Requesting device orientation permission...');
      
      // Check if DeviceOrientationEvent exists
      if (typeof DeviceOrientationEvent === 'undefined') {
        throw new Error('Device orientation not supported on this device');
      }

      // For iOS 13+, we need explicit permission
      if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
        const permission = await (DeviceOrientationEvent as any).requestPermission();
        console.log('[Compass] Permission response:', permission);
        
        if (permission !== 'granted') {
          throw new Error('Permission denied for device orientation');
        }
      }

      setPermissionGranted(true);
      setIsListening(true);
      console.log('[Compass] Permission granted, starting listener');

      // Add orientation listeners
      // Use deviceorientationabsolute for compass on supported devices (Android)
      window.addEventListener('deviceorientationabsolute', handleOrientation as any, true);
      // Fallback to regular deviceorientation (iOS uses this with webkitCompassHeading)
      window.addEventListener('deviceorientation', handleOrientation);

    } catch (err: any) {
      console.error('[Compass] Permission error:', err);
      setError(err.message || 'Failed to access device motion sensors');
      setIsListening(false);
    }
  }, [handleOrientation]);

  // Stop listening
  const stopListening = useCallback(() => {
    window.removeEventListener('deviceorientationabsolute', handleOrientation as any, true);
    window.removeEventListener('deviceorientation', handleOrientation);
    setIsListening(false);
    console.log('[Compass] Stopped listening to device orientation');
  }, [handleOrientation]);

  // Handle capture with screenshot
  const handleCapture = useCallback(async () => {
    setIsCapturing(true);
    const cardinalDirection = getCardinalDirection(currentHeading);
    const fullDirection = getFullDirectionName(currentHeading);
    const directionString = `${fullDirection} (${currentHeading.toFixed(1)}°)`;
    console.log('[Compass] 📸 Starting capture process...', {
      direction: directionString,
      heading: currentHeading,
      hasRef: !!screenshotRef.current
    });
    
    let screenshotDataUrl: string | undefined = undefined;
    
    try {
      // Capture screenshot of the measurement display
      if (screenshotRef.current) {
        console.log('[Compass] 🎬 Ref found, taking screenshot...');
        const canvas = await html2canvas(screenshotRef.current, {
          backgroundColor: '#000000',
          scale: 2, // Higher quality
          logging: false,
          useCORS: true
        });
        
        screenshotDataUrl = canvas.toDataURL('image/png');
        console.log('[Compass] ✅ Screenshot captured successfully!', {
          size: screenshotDataUrl.length,
          hasData: !!screenshotDataUrl
        });
      } else {
        console.warn('[Compass] ⚠️ screenshotRef.current is null, cannot take screenshot');
      }
    } catch (error) {
      console.error('[Compass] ❌ Screenshot capture failed:', error);
    }
    
    // Always call the callback with direction and screenshot (if available)
    console.log('[Compass] 📤 Calling onDirectionCaptured with:', {
      direction: directionString,
      hasScreenshot: !!screenshotDataUrl
    });
    onDirectionCaptured(directionString, screenshotDataUrl);
    
    // Clean up
    setIsCapturing(false);
    stopListening();
    onClose();
  }, [currentHeading, onDirectionCaptured, stopListening, onClose]);

  // Start on mount
  useEffect(() => {
    requestPermissionAndStart();

    return () => {
      stopListening();
    };
  }, [requestPermissionAndStart, stopListening]);

  const displayHeading = currentHeading; // Keep as number for calculations
  const displayHeadingText = currentHeading.toFixed(1); // Formatted text for display
  const cardinalDirection = getCardinalDirection(displayHeading);
  const fullDirectionName = getFullDirectionName(displayHeading);

  // Visual indicator: color based on cardinal directions
  const getDirectionColor = (heading: number) => {
    const h = Math.round(heading / 45) % 8;
    const colors = [
      'text-red-500',      // N (0°)
      'text-orange-500',   // NE (45°)
      'text-yellow-500',   // E (90°)
      'text-lime-500',     // SE (135°)
      'text-green-500',    // S (180°)
      'text-cyan-500',     // SW (225°)
      'text-blue-500',     // W (270°)
      'text-purple-500'    // NW (315°)
    ];
    return colors[h];
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col overflow-hidden" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }}>
      {/* Compact Header with iOS Safe Area */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white shadow-lg flex-shrink-0 px-4 app-header-padding">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold flex items-center space-x-2">
            <Navigation className="w-5 h-5" />
            <span>Compass</span>
          </h2>
          <button
            onClick={() => {
              stopListening();
              onClose();
            }}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Content - No Scroll - Screenshot Target */}
      <div ref={screenshotRef} className="flex-1 flex flex-col items-center justify-center px-6 py-4 overflow-hidden">
        {error ? (
          <div className="text-center">
            <div className="text-red-500 text-lg mb-4">{error}</div>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg font-medium"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Compass Rose */}
            <div className="relative w-72 h-72 mb-6">
              {/* Outer ring with degree markings */}
              <div className="absolute inset-0 rounded-full border-4 border-gray-700">
                {/* All 8 cardinal directions */}
                {['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'].map((dir, i) => {
                  const angle = i * 45;
                  // Check if this direction is active (within ±22.5 degrees)
                  let diff = Math.abs(displayHeading - angle);
                  if (diff > 180) diff = 360 - diff;
                  const isActive = diff < 22.5;
                  const isMajor = i % 2 === 0; // N, E, S, W
                  
                  return (
                    <div
                      key={dir}
                      className={`absolute font-bold ${isActive ? 'text-white scale-125' : 'text-gray-400'} ${isMajor ? 'text-xl' : 'text-base'} transition-all duration-300`}
                      style={{
                        top: '50%',
                        left: '50%',
                        transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(${isMajor ? '-120px' : '-115px'}) rotate(-${angle}deg)`
                      }}
                    >
                      {dir}
                    </div>
                  );
                })}

                {/* Degree markers */}
                {[...Array(36)].map((_, i) => {
                  const angle = i * 10;
                  const isMajor = angle % 90 === 0;
                  const isMinor = angle % 30 === 0;
                  return (
                    <div
                      key={i}
                      className="absolute"
                      style={{
                        top: '50%',
                        left: '50%',
                        transform: `translate(-50%, -50%) rotate(${angle}deg)`,
                        transformOrigin: 'center'
                      }}
                    >
                      <div
                        className={`${isMajor ? 'bg-white h-4 w-1' : isMinor ? 'bg-gray-400 h-3 w-0.5' : 'bg-gray-600 h-2 w-0.5'}`}
                        style={{ marginLeft: '-0.5px', marginTop: '-130px' }}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Rotating needle */}
              <div
                className="absolute inset-0 flex items-center justify-center transition-transform duration-300 ease-out"
                style={{ transform: `rotate(${displayHeading}deg)` }}
              >
                <div className="relative">
                  {/* North pointer (red) */}
                  <div className="absolute left-1/2 -translate-x-1/2 -top-24 w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-b-[80px] border-b-red-500 drop-shadow-lg" />
                  {/* South pointer (white) */}
                  <div className="absolute left-1/2 -translate-x-1/2 top-4 w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-t-[80px] border-t-white drop-shadow-lg" />
                  {/* Center circle */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-gray-800 rounded-full border-2 border-white" />
                </div>
              </div>

              {/* Center heading display */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-black/80 backdrop-blur-sm rounded-lg px-4 py-2 border border-gray-700">
                  <div className={`text-3xl font-bold ${getDirectionColor(displayHeading)} transition-colors duration-300`}>
                    {cardinalDirection}
                  </div>
                  <div className="text-lg text-gray-300 text-center">
                    {displayHeadingText}°
                  </div>
                </div>
              </div>
            </div>

            {/* Direction Name */}
            <div className="text-center mb-6">
              <div className="text-2xl text-white font-semibold">
                {fullDirectionName}
              </div>
            </div>

            {/* Instructions - Compact */}
            <div className="bg-gray-900 rounded-lg p-4 max-w-sm border border-gray-800 mb-4">
              <h3 className="text-white font-semibold mb-2 text-sm">How to Use</h3>
              <ol className="text-gray-400 text-xs space-y-1">
                <li>1. Hold phone flat (parallel to ground)</li>
                <li>2. Move away from metal objects</li>
                <li>3. Wait for compass to stabilize</li>
                <li>4. Tap button below to save direction</li>
              </ol>
            </div>

            {/* Status Indicator - Compact */}
            {isListening && (
              <div className="inline-flex items-center space-x-2 bg-green-900/30 text-green-400 px-3 py-1.5 rounded-full border border-green-700">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-xs font-medium">Reading compass...</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Capture Button - Fixed at Bottom with iOS Safe Area */}
      {!error && (
        <div className="bg-gray-900 border-t border-gray-800 flex-shrink-0" style={{ paddingTop: '16px', paddingLeft: '16px', paddingRight: '16px', paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}>
          <button
            onClick={handleCapture}
            disabled={!isListening || isCapturing}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-700 text-white py-3.5 rounded-xl font-bold text-base shadow-lg hover:from-indigo-700 hover:to-purple-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            <Check className="w-5 h-5" />
            <span>{isCapturing ? 'Capturing...' : `Use This Direction (${cardinalDirection})`}</span>
          </button>
        </div>
      )}
    </div>,
    document.body
  );
};
